import { useState } from 'react';
import { useNotification } from '@/hooks';
import { NotificationSeverity } from '@/components/notifications/Notification';
import {
  useLazyGetAllCommercesQuery,
  useLazyGetMeliSellersByCommerceCodeQuery,
} from '../services';
import { CommerceFilters } from '../models';
import { toCommerceQueryFilters } from '../utils';

const todayStamp = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

/**
 * Exporta a Excel todos los comercios que cumplen los filtros aplicados, con
 * dirección, contacto y seller ID de Mercado Libre. Si meli integra no
 * responde, el archivo sale igual, con la columna de seller ID vacía.
 */
const useCommercesExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const { showNotification } = useNotification();
  const [fetchAllCommerces] = useLazyGetAllCommercesQuery();
  const [fetchSellers] = useLazyGetMeliSellersByCommerceCodeQuery();

  const exportCommerces = async (filters?: CommerceFilters) => {
    setIsExporting(true);
    try {
      const sellersResult = await fetchSellers(undefined, true);
      const sellersByCode = sellersResult.data ?? {};

      if (filters?.sellerId?.trim() && !sellersResult.data) {
        showNotification(
          'No se pudo consultar Mercado Libre para buscar por Seller ID.',
          NotificationSeverity.error
        );
        return;
      }

      const commerces = await fetchAllCommerces({
        filters: filters && toCommerceQueryFilters(filters, sellersByCode),
      }).unwrap();

      if (commerces.length === 0) {
        showNotification(
          'No hay comercios para exportar con esos filtros.',
          NotificationSeverity.warning
        );
        return;
      }

      const { buildCommercesWorkbook } = await import(
        '../utils/buildCommercesWorkbook'
      );
      const blob = await buildCommercesWorkbook(commerces, sellersByCode);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Comercios-${todayStamp()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      showNotification(
        'No se pudieron exportar los comercios. Intentá de nuevo.',
        NotificationSeverity.error
      );
    } finally {
      setIsExporting(false);
    }
  };

  return { exportCommerces, isExporting };
};

export default useCommercesExport;
